import React, { memo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useProject } from '../../context/ProjectContext';
import { notificationsApi } from '../../services/api/notifications';
import { formatTime } from '../../utils/formatters';

// ============================================
// Icons
// ============================================

const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const BellIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// ============================================
// Component
// ============================================

export const Navbar: React.FC = memo(() => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { setMobileSidebarOpen } = useUI();
  const { notifications, setNotifications } = useProject();
  
  const [showNotifs, setShowNotifs] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const readCount = notifications.filter(n => n.isRead).length;

  const handleLogout = useCallback(async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Logout error', err);
    }
  }, [signOut, navigate]);

  const markAllRead = useCallback(async () => {
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    
    try {
      const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
      await Promise.all(unreadIds.map(id => notificationsApi.markAsRead(id)));
    } catch (err) {
      console.error('Failed to mark notifications as read', err);
    }
  }, [notifications, setNotifications]);

  const deleteReadNotifications = useCallback(async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    
    try {
      const userId = notifications[0]?.userId;
      if (userId) {
        await notificationsApi.deleteRead(userId);
        setNotifications(prev => prev.filter(n => !n.isRead));
      }
    } catch (err) {
      console.error('Failed to delete read notifications', err);
    } finally {
      setIsDeleting(false);
    }
  }, [notifications, setNotifications, isDeleting]);

  const handleDeleteNotification = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    
    try {
      await notificationsApi.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification', err);
    }
  }, [setNotifications]);

  const handleNotificationClick = useCallback(async (id: string, link?: string) => {
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    
    try {
      await notificationsApi.markAsRead(id);
    } catch (err) {
      console.error('Failed to update notification', err);
    }
    
    if (link) {
      navigate(link);
    }
    setShowNotifs(false);
  }, [setNotifications, navigate]);

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 md:px-10 flex items-center justify-between sticky top-0 z-[100] pt-[env(safe-area-inset-top)] min-h-[calc(4rem+env(safe-area-inset-top))] md:min-h-[calc(5rem+env(safe-area-inset-top))]">
      {/* Left: Mobile Menu Button */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => setMobileSidebarOpen(true)}
          className="md:hidden p-3 text-slate-600 hover:bg-slate-100 rounded-xl transition-all active:scale-95"
          aria-label="Open menu"
        >
          <MenuIcon />
        </button>
      </div>

      {/* Center: Search (Desktop) */}
      <div className="flex-1 max-w-xl mx-4 md:mx-8 hidden lg:block">
        <div className="relative group">
          <input 
            type="text" 
            placeholder="Search everything..." 
            className="
              w-full pl-12 pr-4 py-2.5 
              bg-slate-100/50 border border-transparent 
              focus:bg-white focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 
              rounded-2xl text-sm outline-none transition-all 
              placeholder-slate-400 font-medium
            "
          />
          <div className="absolute left-4 top-3 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
            <SearchIcon />
          </div>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2 md:gap-6">
        {/* Notifications */}
        <div className="relative">
          <button 
            onClick={() => setShowNotifs(!showNotifs)}
            className={`
              p-2 md:p-2.5 rounded-xl relative transition-all duration-300
              ${showNotifs ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:bg-slate-50'}
            `}
            aria-label="Notifications"
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center rounded-full border-2 border-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifs && (
            <>
              <div 
                className="fixed inset-0 z-[110]" 
                onClick={() => setShowNotifs(false)}
                aria-hidden="true"
              />
              <div className="
                fixed md:absolute right-2 left-2 md:left-auto md:right-0 mt-4 md:w-96 
                bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] 
                border border-slate-100 overflow-hidden z-[120] 
                animate-in fade-in slide-in-from-top-4 duration-300
              ">
                {/* Header */}
                <div className="p-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                  <h3 className="font-extrabold text-slate-900 text-base">Notifications</h3>
                  <div className="flex gap-3">
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllRead}
                        className="text-[10px] text-indigo-600 font-bold hover:text-indigo-700 underline underline-offset-4 decoration-indigo-200"
                      >
                        Mark all read
                      </button>
                    )}
                    {readCount > 0 && (
                      <button 
                        onClick={deleteReadNotifications}
                        disabled={isDeleting}
                        className="text-[10px] text-rose-600 font-bold hover:text-rose-700 underline underline-offset-4 decoration-rose-200 disabled:opacity-50"
                      >
                        Delete read
                      </button>
                    )}
                  </div>
                </div>

                {/* Notification List */}
                <div className="max-h-[70vh] md:max-h-[400px] overflow-y-auto no-scrollbar">
                  {notifications.length === 0 ? (
                    <div className="p-12 text-center text-slate-400">
                      <p className="text-xs font-medium uppercase tracking-widest">Inbox is clear</p>
                    </div>
                  ) : (
                    notifications.map(n => (
                      <div 
                        key={n.id} 
                        className={`
                          w-full relative group border-b border-slate-50 transition-all flex gap-4
                          ${!n.isRead ? 'bg-indigo-50/20' : 'bg-white'}
                        `}
                      >
                        <button 
                          onClick={() => handleNotificationClick(n.id, n.link)}
                          className="flex-1 text-left p-4 flex gap-3 hover:bg-indigo-50/50 transition-all"
                        >
                          <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${!n.isRead ? 'bg-indigo-500' : 'bg-slate-200'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-bold transition-colors ${!n.isRead ? 'text-slate-900' : 'text-slate-500'}`}>
                              {n.title}
                            </p>
                            <p className="text-[11px] text-slate-600 mt-1 leading-relaxed line-clamp-2">
                              {n.message}
                            </p>
                            <p className="text-[9px] text-slate-400 mt-2 font-medium">
                              {formatTime(n.timestamp)}
                            </p>
                          </div>
                        </button>
                        <button 
                          onClick={(e) => handleDeleteNotification(e, n.id)}
                          className="absolute right-3 top-3 p-1.5 text-slate-300 hover:text-rose-500 md:opacity-0 md:group-hover:opacity-100 transition-all"
                          aria-label="Delete notification"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Logout Button */}
        <button
          type="button"
          onClick={handleLogout}
          className="
            px-3 md:px-4 py-2
            text-[10px] md:text-xs font-black uppercase tracking-widest
            text-slate-700 hover:text-rose-600 hover:bg-rose-50
            rounded-xl transition-all border border-slate-200
          "
        >
          Logout
        </button>
      </div>
    </header>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;
